import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Calculate, History, Refresh, Search, ShowChart } from '@mui/icons-material';

import { colors, fontSize } from '../../theme';

function HeaderBar({
  metaScoreDate,
  source,
  lookupTicker,
  onLookupChange,
  onLookupKeyDown,
  onOpenLookup,
  calculating,
  onCalculate,
  onRefresh,
  t,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            flexShrink: 0,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.24)',
          }}
        >
          <ShowChart sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: '1.35rem',
                fontWeight: 800,
                color: colors.gray[800],
                letterSpacing: -0.3,
                lineHeight: 1.2,
              }}
            >
              {t('ichimokuTransition.title')}
            </Typography>
            {metaScoreDate ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: colors.gray[100],
                  px: 0.9,
                  py: 0.2,
                  borderRadius: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: colors.info.main,
                    boxShadow: `0 0 5px ${colors.info.main}`,
                  }}
                />
                <Typography sx={{ fontSize: fontSize.xs, fontWeight: 700, color: colors.gray[600] }}>
                  {metaScoreDate}
                </Typography>
              </Box>
            ) : null}
            <Chip
              label={source === 'live' ? t('ichimokuTransition.live') : t('ichimokuTransition.persisted')}
              size="small"
              sx={{
                height: 22,
                fontSize: fontSize.xxs,
                fontWeight: 700,
                bgcolor: source === 'live' ? '#ecfeff' : colors.gray[50],
                color: source === 'live' ? '#0891b2' : colors.gray[600],
              }}
            />
          </Box>
          <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[400], fontWeight: 500, mt: 0.2 }}>
            {t('ichimokuTransition.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={lookupTicker}
          onChange={onLookupChange}
          onKeyDown={onLookupKeyDown}
          placeholder={t('ichimokuTransition.lookupPlaceholder')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: colors.gray[400] }} />
                </InputAdornment>
              ),
              endAdornment: lookupTicker ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={onOpenLookup} sx={{ p: 0.25, color: colors.info.main }}>
                    <History sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{
            width: { xs: '100%', sm: 200 },
            '& .MuiOutlinedInput-root': {
              fontSize: fontSize.md,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: colors.background.paper,
              '& fieldset': { borderColor: colors.gray[200] },
              '&:hover fieldset': { borderColor: colors.gray[300] },
            },
            '& input::placeholder': { fontFamily: 'inherit', fontWeight: 400, letterSpacing: 'normal' },
          }}
        />
        <Button
          variant="contained"
          startIcon={calculating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Calculate />}
          disabled={calculating}
          onClick={onCalculate}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
            borderRadius: '4px',
            bgcolor: colors.info.main,
            '&:hover': { bgcolor: colors.info.dark },
            '& .MuiButton-startIcon': { mr: 0.5 },
          }}
        >
          {calculating ? t('ichimokuTransition.calculating') : t('ichimokuTransition.calculate')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefresh}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
            borderRadius: '4px',
            color: colors.gray[500],
            borderColor: colors.gray[200],
            '&:hover': { bgcolor: colors.gray[50], borderColor: colors.gray[300] },
            '& .MuiButton-startIcon': { mr: 0.5 },
          }}
        >
          {t('ichimokuTransition.refresh')}
        </Button>
      </Box>
    </Box>
  );
}

HeaderBar.propTypes = {
  metaScoreDate: PropTypes.string,
  source: PropTypes.string.isRequired,
  lookupTicker: PropTypes.string.isRequired,
  onLookupChange: PropTypes.func.isRequired,
  onLookupKeyDown: PropTypes.func.isRequired,
  onOpenLookup: PropTypes.func.isRequired,
  calculating: PropTypes.bool.isRequired,
  onCalculate: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

HeaderBar.defaultProps = {
  metaScoreDate: null,
};

export default HeaderBar;
